import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { isBefore, parseISO } from 'date-fns'
import { get } from 'lodash-es'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'

import { episodeStates } from '../../../../common/constants/appConstants'
import { CLIENT_NOTES } from '../../../../common/constants/routingConstants'
import { getAuthConfig, getAuthUserId } from '../../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../../data/lyraTherapy/clientSelectors'
import { getProviderEpisodes, isProviderEpisode } from '../../../episodes/data/episodeParticipants'
import { useAllEpisodesForSelectedClient } from '../../../episodes/data/hooks/useAllEpisodesForSelectedClient'

const EMPTY_EPISODES: Episode[] = []

export const useSelectedEpisodeId = (): [
  string | undefined,
  (selectedEpisode: Episode) => void,
  Episode | undefined,
  boolean,
] => {
  const { pathname } = useLocation()
  const selectedClientId = useSelector(getSelectedClientId)
  const userId = useSelector(getAuthUserId)
  const openClientAccessSupported = useSelector(getAuthConfig)?.openClientAccessSupported ?? false
  const { isDBTTeensEnabled } = useFlags()
  const {
    data: allEpisodes = EMPTY_EPISODES,
    isFetching: isAllEpisodesFetching,
    isUninitialized: isAllEpisodesUninitialized,
  } = useAllEpisodesForSelectedClient()

  /**
   * The last provider-specific episode is tracked because the Notes tab shows all episodes, even for other providers.
   * We track the last provider-specific episode so that if the user selects an episode that is not available on other tabs
   * we can reset back to the last episode that is available on other tabs.
   */
  const [lastProviderSpecificEpisode, setLastProviderSpecificEpisode] = useState<Episode | undefined>(undefined)

  /**
   * This is the selected episode. This will be set based on the route, for example, on the Notes tab, we will simply
   * return the value. For other tabs we will check if the selectedEpisode belongs to the current userId (provider),
   * and if not, return the lastProviderSpecificEpisode.
   */
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<string | undefined>(undefined)
  // Scope episode readiness to the client whose episodes were resolved, not a previous selection.
  const [resolvedClientId, setResolvedClientId] = useState<string | null>(null)
  const processedEpisodesRef = useRef<{
    clientId: string
    episodes: Episode[]
    userId: typeof userId
    openClientAccessSupported: boolean
    isDBTTeensEnabled: boolean
  } | null>(null)

  /**
   * When the episodes are first loaded, prefer an active episode that belongs to the current provider, then one where
   * they are an active care-team member. Roles with open client access can view an active DBT Teens episode before
   * they are associated with it, so prefer that episode over a historical provider-owned episode. Preserve the
   * existing provider-owned behavior otherwise.
   */
  useEffect(() => {
    if (!selectedClientId || isAllEpisodesFetching || isAllEpisodesUninitialized) {
      return
    }
    if (
      resolvedClientId === selectedClientId &&
      processedEpisodesRef.current?.clientId === selectedClientId &&
      processedEpisodesRef.current.episodes === allEpisodes &&
      processedEpisodesRef.current.userId === userId &&
      processedEpisodesRef.current.openClientAccessSupported === openClientAccessSupported &&
      processedEpisodesRef.current.isDBTTeensEnabled === isDBTTeensEnabled
    ) {
      return
    }
    processedEpisodesRef.current = {
      clientId: selectedClientId,
      episodes: allEpisodes,
      userId,
      openClientAccessSupported,
      isDBTTeensEnabled,
    }

    if (!allEpisodes?.length) {
      setLastProviderSpecificEpisode(undefined)
      setSelectedEpisodeId(undefined)
      setResolvedClientId(selectedClientId)
      return
    }

    const providerSpecificEpisodes = getProviderEpisodes(allEpisodes, userId)
    const activeProviderEpisode = providerSpecificEpisodes.find(
      (episode) => episode.state === episodeStates.IN_PROGRESS,
    )
    const activeOpenClientEpisode =
      openClientAccessSupported && isDBTTeensEnabled
        ? allEpisodes.find(
            (episode) =>
              episode.state === episodeStates.IN_PROGRESS &&
              episode.program_name === ProgramNames.DialecticalBehaviorTherapyTeens,
          )
        : undefined

    const firstProviderEpisode =
      activeProviderEpisode ||
      activeOpenClientEpisode ||
      providerSpecificEpisodes.toSorted((a, b) =>
        isBefore(parseISO(get(a, 'end_date') as string), parseISO(get(b, 'end_date') as string)) ? 1 : -1,
      )[0]

    setLastProviderSpecificEpisode(firstProviderEpisode)
    setSelectedEpisodeId(firstProviderEpisode?.id)
    setResolvedClientId(selectedClientId)
  }, [
    allEpisodes,
    isDBTTeensEnabled,
    isAllEpisodesFetching,
    isAllEpisodesUninitialized,
    openClientAccessSupported,
    resolvedClientId,
    selectedClientId,
    userId,
  ])

  useEffect(() => {
    if (
      lastProviderSpecificEpisode &&
      !pathname.includes(CLIENT_NOTES.route) &&
      selectedEpisodeId !== lastProviderSpecificEpisode.id
    ) {
      // the user is navigating to a non-Notes tab, and the selectedEpisode is not for the current provider.
      // we need to update the lastProviderSpecificEpisode
      setSelectedEpisodeId(lastProviderSpecificEpisode.id)
    }
  }, [pathname, userId, setSelectedEpisodeId, lastProviderSpecificEpisode]) // eslint-disable-line react-hooks/exhaustive-deps

  const episodeSelectHandler = useCallback(
    (selectedEpisode: Episode): void => {
      if (isProviderEpisode(selectedEpisode, userId)) {
        setLastProviderSpecificEpisode(selectedEpisode)
      }
      setSelectedEpisodeId(selectedEpisode.id)
    },
    [setSelectedEpisodeId, userId],
  )

  const isEpisodeSelectionResolved =
    Boolean(selectedClientId) && resolvedClientId === selectedClientId && !isAllEpisodesUninitialized

  return [selectedEpisodeId, episodeSelectHandler, lastProviderSpecificEpisode, isEpisodeSelectionResolved]
}
